// Acceso sin sesion: aceptar una invitacion, restablecer la contrasena y pedir el enlace para hacerlo (D24).
// Los enlaces de los correos del motor apuntan a <empresa>-admin.crm...; Caddy los trae aqui.
import { metadataQuery, setSession, type Session } from "./http";
import { DOMINIO, empresaDelHost } from "../lib/direcciones";

export type EmpresaInvitacion = { id: string; nombre: string; logo?: string };

export async function empresaDeInvitacion(
  hash: string,
): Promise<EmpresaInvitacion | null> {
  const { findWorkspaceFromInviteHash: w } = await metadataQuery<{
    findWorkspaceFromInviteHash: {
      id: string;
      displayName?: string;
      logo?: string;
    } | null;
  }>(
    "query($h:String!){ findWorkspaceFromInviteHash(inviteHash:$h){ id displayName logo } }",
    { h: hash },
  );
  return w
    ? {
        id: w.id,
        nombre: w.displayName || "tu empresa",
        logo: w.logo || undefined,
      }
    : null;
}

async function abrirSesion(
  loginToken: string,
  origen: string,
  email: string,
  empresa: EmpresaInvitacion,
) {
  const { getAuthTokensFromLoginToken: r } = await metadataQuery<{
    getAuthTokensFromLoginToken: {
      tokens: {
        accessOrWorkspaceAgnosticToken: { token: string; expiresAt: string };
        refreshToken: { token: string };
      };
    };
  }>(
    "mutation($t:String!,$o:String!){ getAuthTokensFromLoginToken(loginToken:$t, origin:$o){ tokens{ accessOrWorkspaceAgnosticToken{ token expiresAt } refreshToken{ token } } } }",
    { t: loginToken, o: origen },
  );
  const s: Session = {
    modo: "usuario",
    accessToken: r.tokens.accessOrWorkspaceAgnosticToken.token,
    expiresAt: r.tokens.accessOrWorkspaceAgnosticToken.expiresAt,
    refreshToken: r.tokens.refreshToken.token,
    email,
    workspace: {
      id: empresa.id,
      nombre: empresa.nombre,
      url: origen,
      logo: empresa.logo,
    },
  };
  return s;
}

/** Si el correo ya tiene usuario en el CRM (un solo usuario por persona para todas sus empresas). */
export async function correoTieneCuenta(email: string): Promise<boolean> {
  const { checkUserExists: r } = await metadataQuery<{
    checkUserExists: { exists: boolean };
  }>("query($e:String!){ checkUserExists(email:$e){ exists } }", {
    e: email.trim(),
  });
  return r.exists;
}

/** Crea la cuenta (o, si el correo ya tiene cuenta, entra con su clave) dentro de la empresa que invita. */
export async function aceptarInvitacion(p: {
  hash: string;
  tokenPersonal?: string;
  email: string;
  clave: string;
  nombre: string;
  apellido: string;
  empresa: EmpresaInvitacion;
}) {
  const { signUpInWorkspace: r } = await metadataQuery<{
    signUpInWorkspace: {
      loginToken: { token: string };
      workspace: { id: string; workspaceUrls: { subdomainUrl: string } };
    };
  }>(
    "mutation($e:String!,$p:String!,$w:UUID!,$h:String,$t:String){ signUpInWorkspace(email:$e,password:$p,workspaceId:$w,workspaceInviteHash:$h,workspacePersonalInviteToken:$t){ loginToken{ token } workspace{ id workspaceUrls{ subdomainUrl } } } }",
    {
      e: p.email.trim(),
      p: p.clave,
      w: p.empresa.id,
      h: p.hash,
      t: p.tokenPersonal || null,
    },
  );
  const origen = r.workspace.workspaceUrls.subdomainUrl.replace(/\/$/, "");
  const s = await abrirSesion(
    r.loginToken.token,
    origen,
    p.email.trim(),
    p.empresa,
  );
  // Su nombre en el CRM (el registro solo pide correo y clave).
  if (p.nombre.trim() || p.apellido.trim()) {
    const { currentUser } = await metadataQuery<{
      currentUser: { workspaceMember: { id: string } | null };
    }>("{ currentUser { workspaceMember { id } } }", undefined, s.accessToken);
    const id = currentUser.workspaceMember?.id;
    if (id)
      await fetch(`/rest/workspaceMembers/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${s.accessToken}`,
        },
        body: JSON.stringify({
          name: { firstName: p.nombre.trim(), lastName: p.apellido.trim() },
        }),
      }).catch(() => {});
  }
  setSession(s);
}

export async function validarEnlaceClave(
  token: string,
): Promise<{ email: string } | null> {
  const { validatePasswordResetToken: r } = await metadataQuery<{
    validatePasswordResetToken: { email: string } | null;
  }>(
    "query($t:String!){ validatePasswordResetToken(passwordResetToken:$t){ id email } }",
    { t: token },
  );
  return r;
}

export async function cambiarClaveConEnlace(token: string, nueva: string) {
  const { updatePasswordViaResetToken: r } = await metadataQuery<{
    updatePasswordViaResetToken: { success: boolean };
  }>(
    "mutation($t:String!,$p:String!){ updatePasswordViaResetToken(passwordResetToken:$t, newPassword:$p){ success } }",
    { t: token, p: nueva },
  );
  if (!r.success)
    throw new Error("No se pudo cambiar la contraseña. Pide un enlace nuevo.");
}

/** Solo en la direccion de una empresa (<empresa>.crm...): el enlace se pide para esa empresa. */
export async function pedirEnlaceClave(email: string) {
  const empresa = empresaDelHost();
  if (!empresa)
    throw new Error(
      `Entra por la dirección de tu empresa (<empresa>.${DOMINIO}) para pedir el enlace.`,
    );
  const { getPublicWorkspaceDataByDomain: w } = await metadataQuery<{
    getPublicWorkspaceDataByDomain: { id: string } | null;
  }>("query($o:String!){ getPublicWorkspaceDataByDomain(origin:$o){ id } }", {
    o: `https://${empresa}-admin.${DOMINIO}`,
  });
  if (!w) throw new Error("No encontramos la empresa de esta dirección.");
  await metadataQuery(
    "mutation($e:String!,$w:UUID!){ emailPasswordResetLink(email:$e, workspaceId:$w){ success } }",
    { e: email.trim(), w: w.id },
  );
}

/** Desde Mi perfil: envia a mi correo el enlace para crear una contrasena nueva. */
export const enviarEnlaceClave = (email: string, workspaceId: string) =>
  metadataQuery(
    "mutation($e:String!,$w:UUID!){ emailPasswordResetLink(email:$e, workspaceId:$w){ success } }",
    { e: email.trim(), w: workspaceId },
  );
